import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Tab, Tabs, Paper, Typography } from '@mui/material';
import { Description as FormIcon } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FormBuilderComponent, FormRenderer } from '../components/formio';
import { useAuth } from '../hooks/useAuth';
import DataContainer, { TableActionButton, TableActionGroup } from '../components/common/DataContainer';
import EnhancedConfirmationDialog from '../components/common/custom-component/EnhancedConfirmationDialog';
import LoadingBackdrop from '../components/common/custom-component/LoadingBackdrop';
import Loader from '../components/common/custom-component/Loader';
import { useToast } from '../components/common/custom-component/ToastProvider';
import { fetchFormioFormsThunk, deleteFormioFormThunk } from '../store/thunks/formioThunks';
import { useSubmitFormioSubmission } from '../hooks/useSubmitFormioSubmission';
import { logger } from 'helpers/logger';
import { fetchDatabaseRecords, fetchDatabaseRecord } from "../store/thunks/mdsThunks";

const FormsManagementPage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [forms, setForms] = useState([]);
  const [isFetchingForms, setIsFetchingForms] = useState(false);
  const [formsSubmission, setFormsSubmission] = useState([]);
  const [isFetchingFormsSubmission, setIsFetchingFormsSubmission] = useState(false);
  const [selectedFormId, setSelectedFormId] = useState(null);
  const [continueFillingData, setContinueFillingData] = useState(null);
  const [editingFormId, setEditingFormId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [formToDelete, setFormToDelete] = useState(null);
  const [isLoadingBackdrop, setIsLoadingBackdrop] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [t] = useTranslation();
  const { success: ToastSuccess, error: ToastError } = useToast();
  const keycloakAuth = useAuth();

  const { submitSubmission } = useSubmitFormioSubmission();

  const activeThunksRef = useRef([]);

  useEffect(() => {
    return () => {
      activeThunksRef.current.forEach((thunk) => {
        if (thunk && typeof thunk.abort === 'function') thunk.abort();
      });
      activeThunksRef.current = [];
    };
  }, []);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Reset selections when changing tabs
    if (newValue !== 2) {
      setSelectedFormId(null);
      setContinueFillingData(null);
    }
    if (newValue !== 3) setEditingFormId(null);
  };

  const isSystemForm = (form) => {
    const systemForms = ['user', 'admin', 'user/login', 'user/register', 'admin/login'];
    return systemForms.includes(form.path) || form.type === 'resource';
  };

  const fetchForms = useCallback(async () => {
    setIsFetchingForms(true);

    try {
      const thunkPromise = dispatch(fetchFormioFormsThunk());
      activeThunksRef.current.push(thunkPromise);

      const result = await thunkPromise;

      activeThunksRef.current = activeThunksRef.current.filter((x) => x !== thunkPromise);

      if (fetchFormioFormsThunk.fulfilled.match(result)) {
        setForms(result.payload?.data || []);
      } else if (fetchFormioFormsThunk.rejected.match(result)) {
        const msg = result.payload || t('An error occured, please try again later.');
        ToastError(msg, { duration: null });
      }
    } catch (e) {
      logger.error('Unexpected error in fetchForms:', e);
      ToastError(t('An unexpected error occurred'), { duration: null });
    } finally {
      setIsFetchingForms(false);
    }
  }, [dispatch, ToastError, t]);


    const formSubmissionsColumns = useMemo(
    () => [
      { key: 'submission_id', label: 'Submission ID', render: (formSubmission) => formSubmission.submission_id},
      { key: 'form_id', label: 'Form ID', render: (formSubmission) => formSubmission.form_id },
      { key: 'created_at', label: 'Created At', render: (formSubmission) => formSubmission.created_at },
      { key: 'created_by', label: 'Created By', render: (formSubmission) => formSubmission.created_by },
      { key: 'updated_at', label: 'Updated At', render: (formSubmission) => formSubmission.updated_at },
      { key: 'updated_by', label: 'Updated By', render: (formSubmission) => formSubmission.updated_by },
      { key: 'actions', label: 'Action', render: () => null },
    ],
    []
  );

  const formSubmissionColumnKeys = useMemo(
    () => formSubmissionsColumns.filter((c) => c.key !== 'actions').map((c) => c.key).join(','),
    [formSubmissionsColumns]
  );

  const fetchFormsSubmission = useCallback(async () => {
    setIsFetchingFormsSubmission(true);

    try {
      const thunkPromise = dispatch(fetchDatabaseRecords({ tableName: "tbl_form_submission", condition: `process_instance_id=is.null`, columns: formSubmissionColumnKeys }));
      activeThunksRef.current.push(thunkPromise);

      const result = await thunkPromise;

      activeThunksRef.current = activeThunksRef.current.filter((x) => x !== thunkPromise);

      if (fetchDatabaseRecords.fulfilled.match(result)) {
        setFormsSubmission(result.payload?.data?.records || []);
      } else if (fetchDatabaseRecords.rejected.match(result)) {
        const msg = result.payload || t('An error occured, please try again later.');
        ToastError(msg, { duration: null });
      }
    } catch (e) {
      logger.error('Unexpected error in fetchFormsSubmission:', e);
      ToastError(t('An unexpected error occurred'), { duration: null });
    } finally {
      setIsFetchingFormsSubmission(false);
    }
  }, [dispatch, ToastError, t, formSubmissionColumnKeys]);

  useEffect(() => {
    if (activeTab === 1) {
      fetchForms();
    }
  }, [activeTab, fetchForms]);

    useEffect(() => {
    if (activeTab === 4) {
      fetchFormsSubmission();
    }
  }, [activeTab, fetchFormsSubmission]);

  const handleDeleteClick = useCallback((form) => {
    setFormToDelete(form);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
    setFormToDelete(null);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!formToDelete) return;

    setLoadingMessage(t('Deleting form, please wait...'));
    setIsLoadingBackdrop(true);

    try {
      const thunkPromise = dispatch(
        deleteFormioFormThunk({
          formId: formToDelete._id,
        })
      );

      activeThunksRef.current.push(thunkPromise);

      const result = await thunkPromise;

      activeThunksRef.current = activeThunksRef.current.filter((x) => x !== thunkPromise);

      if (deleteFormioFormThunk.fulfilled.match(result)) {
        ToastSuccess(t('Form "{{name}}" deleted successfully!', { name: formToDelete.title || formToDelete.name }));
        setDeleteDialogOpen(false);
        setFormToDelete(null);
        fetchForms();
      } else if (deleteFormioFormThunk.rejected.match(result)) {
        const msg = result.payload || t('An error occured, please try again later.');
        ToastError(msg, { duration: null });
      }
    } catch (e) {
      logger.error('Unexpected error in handleDeleteConfirm:', e);
      ToastError(t('An unexpected error occurred'), { duration: null });
    } finally {
      setIsLoadingBackdrop(false);
    }
  }, [dispatch, formToDelete, ToastSuccess, ToastError, t, fetchForms]);

  const handleFormSubmit = useCallback(
    async ({ changedFields }) => {
      setLoadingMessage(t('Submitting form, please wait...'));
      setIsLoadingBackdrop(true);

      const success = await submitSubmission({
        formId: selectedFormId,
        submissionId: continueFillingData?.submissionId,
        changedFields,
      });

      if (success) {
        setContinueFillingData(null);
        setActiveTab(1);
      }

      setIsLoadingBackdrop(false);
    },
    [submitSubmission, selectedFormId, continueFillingData, t]
  );

  // FormRenderer takes a submissions array (so it can prefetch/cache several
  // forms at once for the View History stepper) - memoized so this stays
  // referentially stable across unrelated re-renders of this page, since
  // FormRenderer treats a new array reference as a reason to refetch.
  const fillFormSubmissions = useMemo(
    () => (selectedFormId ? [{ formId: selectedFormId, data: continueFillingData?.data }] : []),
    [selectedFormId, continueFillingData]
  );

  // Shared by Continue Filling / Execute Task / View History - fetches a
  // submission's payload column and returns it as a parsed, submit-stripped
  // object, or null (after showing a toast) on failure. Caller owns loading
  // state/tab navigation since each of the three uses differs there.
  const fetchSubmissionPayload = useCallback(
    async (submissionId) => {
      const thunkPromise = dispatch(
        fetchDatabaseRecord({
          tableName: 'tbl_form_submission',
          fieldName: 'submission_id',
          itemId: submissionId,
          columns: 'payload',
        })
      );

      activeThunksRef.current.push(thunkPromise);

      const result = await thunkPromise;

      activeThunksRef.current = activeThunksRef.current.filter((x) => x !== thunkPromise);

      if (fetchDatabaseRecord.rejected.match(result)) {
        const msg = result.payload || t('An error occured, please try again later.');
        ToastError(msg, { duration: null });
        return null;
      }

      const rawPayload = result.payload?.data?.record?.payload;

      // The backend may return the payload column as a JSON-stringified
      // string (current behavior) or, potentially, as an already-parsed
      // object - handle both rather than assuming one shape.
      let parsedPayload = {};
      if (typeof rawPayload === 'string') {
        try {
          parsedPayload = JSON.parse(rawPayload);
        } catch (parseError) {
          logger.error('Failed to parse submission payload:', parseError, rawPayload);
        }
      } else if (rawPayload && typeof rawPayload === 'object') {
        parsedPayload = rawPayload;
      }

      // Defensive: also strip "submit" here in case this submission was
      // saved before handleFormSubmit started omitting it.
      const { submit, ...payload } = parsedPayload;
      return payload;
    },
    [dispatch, ToastError, t]
  );

  const handleContinueFilling = useCallback(
    async (formSubmission) => {
      setLoadingMessage(t('Loading submission, please wait...'));
      setIsLoadingBackdrop(true);

      try {
        const payload = await fetchSubmissionPayload(formSubmission.submission_id);
        if (payload) {
          setContinueFillingData({ submissionId: formSubmission.submission_id, data: payload });
          setSelectedFormId(formSubmission.form_id);
          setActiveTab(2);
        }
      } catch (e) {
        logger.error('Unexpected error in handleContinueFilling:', e);
        ToastError(t('An unexpected error occurred'), { duration: null });
      } finally {
        setIsLoadingBackdrop(false);
      }
    },
    [fetchSubmissionPayload, ToastError, t]
  );

  // Mock-only handlers standing in for the future pending-task/history pages
  // (see conversation) - same fetch, then navigate to the one reusable
  // FormRendererPage, which renders read-only or not based on state.readOnly.
  const handleExecuteTask = useCallback(
    async (formSubmission) => {
      setLoadingMessage(t('Loading task, please wait...'));
      setIsLoadingBackdrop(true);

      try {
        const payload = await fetchSubmissionPayload(formSubmission.submission_id);
        if (payload) {
          navigate('/formRenderer', {
            state: {
              submissions: [
                {
                  formId: formSubmission.form_id,
                  submissionId: formSubmission.submission_id,
                  data: payload,
                },
              ],
              readOnly: false,
              title: t('Execute Task'),
              returnUrl: '/manageForms',
            },
          });
        }
      } catch (e) {
        logger.error('Unexpected error in handleExecuteTask:', e);
        ToastError(t('An unexpected error occurred'), { duration: null });
      } finally {
        setIsLoadingBackdrop(false);
      }
    },
    [fetchSubmissionPayload, navigate, ToastError, t]
  );

  const handleViewHistory = useCallback(
    async (formSubmission) => {
      setLoadingMessage(t('Loading history, please wait...'));
      setIsLoadingBackdrop(true);

      try {
        const payload = await fetchSubmissionPayload(formSubmission.submission_id);
        if (payload) {
          navigate('/formRenderer', {
            state: {
              submissions: [{ formId: formSubmission.form_id, data: payload }],
              readOnly: true,
              title: t('View History'),
              returnUrl: '/manageForms',
            },
          });
        }
      } catch (e) {
        logger.error('Unexpected error in handleViewHistory:', e);
        ToastError(t('An unexpected error occurred'), { duration: null });
      } finally {
        setIsLoadingBackdrop(false);
      }
    },
    [fetchSubmissionPayload, navigate, ToastError, t]
  );

  const columns = useMemo(
    () => [
      { key: 'title', label: 'Title', render: (form) => form.title || form.name },
      { key: 'path', label: 'Path', render: (form) => `/${form.path}` },
      { key: 'created', label: 'Created', render: (form) => new Date(form.created).toLocaleDateString() },
      { key: 'actions', label: 'Action', render: () => null },
    ],
    []
  );

  const rowOverlay = useCallback(
    (form) => (
      <TableActionGroup>
        <TableActionButton
          text={t('Fill')}
          variant="contained"
          size="small"
          onClick={() => {
            setSelectedFormId(form._id);
            setContinueFillingData(null);
            setActiveTab(2);
          }}
        />
        <TableActionButton
          text={t('Edit')}
          variant="outlined"
          size="small"
          onClick={() => {
            setEditingFormId(form._id);
            setActiveTab(3);
          }}
        />
        {!isSystemForm(form) && (
          <TableActionButton
            text={t('Delete')}
            variant="outlined"
            size="small"
            color="error"
            onClick={() => handleDeleteClick(form)}
          />
        )}
      </TableActionGroup>
    ),
    [t, handleDeleteClick]
  );

  const submissionRowOverlay = useCallback(
    (formSubmission) => (
      <TableActionGroup>
        <TableActionButton
          text={t('Continue Filling')}
          variant="contained"
          size="small"
          onClick={() => handleContinueFilling(formSubmission)}
        />
        <TableActionButton
          text={t('Execute Task')}
          variant="contained"
          size="small"
          onClick={() => handleExecuteTask(formSubmission)}
        />
        <TableActionButton
          text={t('View History')}
          variant="outlined"
          size="small"
          onClick={() => handleViewHistory(formSubmission)}
        />
      </TableActionGroup>
    ),
    [t, handleContinueFilling, handleExecuteTask, handleViewHistory]
  );

  if (!keycloakAuth.isAuthenticated) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5">Please log in to access forms</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Typography variant="h3" gutterBottom>
        Forms Management
      </Typography>

      <Paper sx={{ width: '100%' }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Build Forms" />
          <Tab label="My Forms" />
          <Tab label="Fill Form" />
          <Tab label="Edit Form" />
          <Tab label="Continue Filling Form" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* Tab 0: Build New Forms */}
          {activeTab === 0 && (
            <FormBuilderComponent
              onSave={() => {
                setActiveTab(1);
              }}
            />
          )}

          {/* Tab 1: List of Forms */}
          {activeTab === 1 && (
            isFetchingForms && forms.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                <Loader variant="dots" message="Loading..." />
              </Box>
            ) : (
              <DataContainer
                data={forms}
                columns={columns}
                rowKey="_id"
                emptyStateIcon={FormIcon}
                emptyStateTitle={t('No forms created yet')}
                emptyStateSubtitle={t('Go to "Build Forms" to create one.')}
                rowOverlay={rowOverlay}
              />
            )
          )}

          {/* Tab 2: Fill Form */}
          {activeTab === 2 && selectedFormId && (
            <FormRenderer
              submissions={fillFormSubmissions}
              onSubmit={handleFormSubmit}
            />
          )}

          {/* Tab 3: Edit Form */}
          {activeTab === 3 && editingFormId && (
            <FormBuilderComponent
              formId={editingFormId}
              onSave={() => {
                setActiveTab(1);
                setEditingFormId(null);
              }}
              onCancel={() => {
                setActiveTab(1);
                setEditingFormId(null);
              }}
            />
          )}

          {/* Tab 4: Continue Filling Form */}
          {activeTab === 4 && (
            isFetchingFormsSubmission && formsSubmission.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                <Loader variant="dots" message="Loading..." />
              </Box>
            ) : (
              <DataContainer
                data={formsSubmission}
                columns={formSubmissionsColumns}
                rowKey="submission_id"
                emptyStateIcon={FormIcon}
                emptyStateTitle={t('No forms had been submitted yet')}
                emptyStateSubtitle={t('Go to "Fill Forms" to submit one.')}
                rowOverlay={submissionRowOverlay}
              />
            )
          )}
        </Box>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <EnhancedConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t('Delete Form?')}
        message={t('Are you sure you want to delete the form "{{name}}"? This action cannot be undone.', {
          name: formToDelete?.title || formToDelete?.name,
        })}
        confirmText={t('Delete')}
        confirmColor="error"
        cancelText={t('Cancel')}
      />

      <LoadingBackdrop open={isLoadingBackdrop} message={loadingMessage} variant="bars" color="greenGradient" />
    </Box>
  );
};

export default FormsManagementPage;
